import React, {useEffect, useState} from 'react';
import {ActivityIndicator, ScrollView, Text, View} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {withDatabase} from '@nozbe/watermelondb/react';
import {withObservables} from '@nozbe/watermelondb/react';
import {Q} from '@nozbe/watermelondb';
import {compose} from 'recompose';
import _ from 'lodash';
import moment from 'moment';

import {EnvironmentalFactor, Patient, Program} from 'src/models';
import {Typography} from 'src/styles';
import ProgramSelect from '../program-select';
import {of} from 'rxjs';
import {oneOf} from '@nozbe/watermelondb/QueryDescription';
import ProgramGraph from '../program-graph';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {useStyle} from 'src/providers/style';
import {Input as DatePeriodInput} from 'src/hook-form-inputs/date-period-buttons';

interface Props {
  patient: Patient;
  clientEnvs: EnvironmentalFactor[];
  startDate: Date;
  endDate: Date;
  filteredPrograms: Program[];
  setExportName?: any;
  client?: any;
}

const ClientReportBeta = ({
  patient,
  clientEnvs = [],
  startDate,
  endDate,
  filteredPrograms,
  setExportName,
  client,
}: Props) => {
  const styles = useStyle();
  const programById = _.keyBy(filteredPrograms, 'id');
  const defaultValues = {
    programId: filteredPrograms?.[0]?.id || '',
  };

  const [isLoading] = useState(false);

  useEffect(() => {
    reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient]);
  const {control, reset} = useForm({defaultValues});
  const selectedProgramId = useWatch({control, name: 'programId'});

  useEffect(() => {
    if (setExportName) {
      const programName = programById[selectedProgramId]?.name;
      const clientName = patient?.lastName;
      const todayDate = moment().format('MM/DD/YYYY');
      setExportName(`${clientName}_${programName}_${todayDate}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgramId, patient]);

  return (
    <>
      {isLoading ? (
        <>
          <ActivityIndicator size={'large'} style={[styles.paddingTop]} />
        </>
      ) : (
        <ScrollView>
          {patient ? (
            <>
              <View style={[styles.padding]}>
                <Text
                  style={[
                    Typography.H1,
                  ]}>{`${patient?.firstName} ${patient?.lastName}`}</Text>
              </View>
              <View style={[styles.paddingHorizontal]}>
                <Controller
                  control={control}
                  rules={{
                    required: 'This is required.',
                  }}
                  render={({field: {onChange, value}}) => (
                    <View style={[styles.flex]}>
                      <ProgramSelect
                        patientId={patient?.id}
                        selected={value}
                        selectProgram={onChange}
                      />
                    </View>
                  )}
                  name="programId"
                  defaultValue={filteredPrograms?.[0]?.id || ''}
                />
              </View>
              {selectedProgramId ? (
                <View style={[styles.paddingHorizontal, styles.alignSelfStart]}>
                  <DatePeriodInput name={'startDate'} watchedValue={client} />
                </View>
              ) : (
                <></>
              )}
              {selectedProgramId ? (
                <ProgramGraph
                  programId={selectedProgramId}
                  envs={clientEnvs}
                  startDate={startDate}
                  endDate={endDate}
                  collapsable={false}
                  showTable={true}
                  reportScreen={true}
                />
              ) : (
                <View style={styles.emptyContainer}>
                  <Icon name="chart-areaspline" size={30} color={'grey'} />
                  <Text style={styles.emptyTitle}>No Program Selected</Text>
                </View>
              )}
            </>
          ) : (
            <View style={styles.emptyContainer}>
              <Icon name="account-supervisor" size={30} color={'grey'} />
              <Text style={styles.emptyTitle}>Please select a client</Text>
            </View>
          )}
        </ScrollView>
      )}
    </>
  );
};

export default compose(
  withDatabase,
  withObservables(['client'], ({client, database}: any) => {
    return {
      patient: database.get('patients').findAndObserve(client),
    };
  }),
  withObservables(['patient', 'filter'], ({patient, database, filter}: any) => {
    const queries = [];
    if (filter?.types && filter.types.length) {
      queries.push(Q.where('type', oneOf(filter.types)));
    }
    if (filter?.programs && filter.programs.length) {
      queries.push(Q.where('id', oneOf(filter.programs)));
    }
    return {
      filteredPrograms: database
        .get('programs')
        .query(
          Q.where('patient_id', patient.id),
          Q.where('state', Q.notEq('archived')),
          Q.where('deleted_at', null),
          ...queries,
        ),
    };
  }),
  withObservables(['patient'], ({patient}: any) => {
    return {
      clientPrograms: patient?.activePrograms || of([]),
      clientEnvs: patient?.activeEnvironmentalFactors || of([]),
    };
  }),
)(ClientReportBeta);
